<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Risk Level</label>
                        <select v-model="filters.risk_level" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="High Risk">High Risk</option>
                            <option value="Low Risk">Low Risk</option>
                            <option value="Safe">Safe</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                        <multiselect v-model="filters.campus_code" :options="campusOptionsFiltered" :multiple="true"
                            :close-on-select="false" placeholder="Select campuses" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Program</label>
                        <multiselect v-model="filters.program" :options="programOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select programs" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
                        <select v-model="filters.gender" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Failed Term</label>
                        <multiselect v-model="filters.failed_term" :options="failedTermOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select failed terms" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Current Reg</label>
                        <select v-model="filters.current_reg" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="2nd Attempt">2nd Attempt</option>
                            <option value="3rd Attempt">3rd Attempt</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Instructor</label>
                        <multiselect v-model="filters.instructor" :options="instructorOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select instructors" track-by="id" label="name">
                        </multiselect>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="container mx-auto px-4 sm:px-8 py-6 space-y-8">
            <div class="flex justify-between items-center flex-wrap gap-4">
                <h1 class="text-2xl font-bold text-gray-800 flex items-center">
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="text-blue-600 mr-2" />
                    <div class="uppercase">Failure Insights Dashboard</div>
                </h1>
                <div class="flex items-center gap-2 text-sm text-slate-500">
                    <span class="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span>Live monitoring mode</span>
                </div>
            </div>

            <section class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div v-for="card in kpis" :key="card.title"
                    class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex flex-col gap-3">
                    <div class="flex items-center justify-between">
                        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ card.title }}</p>
                        <span class="text-lg" :class="card.accent">{{ card.icon }}</span>
                    </div>
                    <p class="text-3xl font-bold text-slate-900">{{ card.value }}</p>
                    <p class="text-xs text-slate-500">{{ card.subtitle }}</p>
                    <div class="text-sm font-semibold flex items-center gap-2"
                        :class="getTrendColorClass(card.trendDirection)">
                        <span>{{ getTrendIcon(card.trendDirection) }}</span>
                        <span>{{ card.trendText }}</span>
                    </div>
                </div>
            </section>

            <section class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <p class="text-xs uppercase tracking-wide text-slate-500">Chart 1</p>
                            <h2 class="text-xl font-semibold text-slate-900">Courses with Highest Failures</h2>
                        </div>
                        <span class="text-xs text-slate-400">Treemap / Bar hybrid</span>
                    </div>
                    <apexchart type="bar" height="320" :options="courseFailureOptions" :series="courseFailureSeries" />
                </div>

                <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <p class="text-xs uppercase tracking-wide text-slate-500">Chart 2</p>
                            <h2 class="text-xl font-semibold text-slate-900">Programs with Highest Risk</h2>
                        </div>
                        <span class="text-xs text-slate-400">Stacked Risk Score</span>
                    </div>
                    <apexchart type="bar" height="320" :options="programRiskOptions" :series="programRiskSeries" />
                </div>
            </section>

            <section class="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <p class="text-xs uppercase tracking-wide text-slate-500">Chart 3</p>
                            <h2 class="text-xl font-semibold text-slate-900">Top Instructors with Failures</h2>
                        </div>
                        <span class="text-xs text-slate-400">Instructor Impact</span>
                    </div>
                    <apexchart type="bar" height="320" :options="instructorFailureOptions"
                        :series="instructorFailureSeries" />
                </div>
                <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 xl:col-span-2">
                    <div class="flex items-center justify-between mb-4">
                        <div>
                            <p class="text-xs uppercase tracking-wide text-slate-500">Chart 4</p>
                            <h2 class="text-xl font-semibold text-slate-900">Failures by Campus</h2>
                        </div>
                        <span class="text-xs text-slate-400">Pie Distribution</span>
                    </div>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <apexchart type="pie" height="320" :options="campusFailureOptions"
                                :series="campusFailureSeries" />
                        </div>
                        <div class="flex flex-col gap-3">
                            <div v-for="campus in campusFailures" :key="campus.name"
                                class="border border-slate-100 rounded-xl px-4 py-3 bg-slate-50">
                                <div class="flex items-center justify-between">
                                    <p class="text-sm font-semibold text-slate-900">{{ campus.name }}</p>
                                    <span class="text-xs font-semibold text-slate-500">{{ campus.percentage }}%</span>
                                </div>
                                <div class="w-full h-2 bg-white rounded-full mt-2 overflow-hidden">
                                    <div class="h-full rounded-full" :class="campus.barClass"
                                        :style="{ width: campus.percentage + '%' }"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-6">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p class="text-xs uppercase tracking-wide text-slate-500">Table</p>
                        <h2 class="text-xl font-semibold text-slate-900">Urgent Intervention List</h2>
                        <p class="text-slate-500 text-sm">Students currently registered for previously failed courses
                        </p>
                    </div>
                    <div class="flex flex-wrap gap-3 text-sm">
                        <button v-for="action in quickActions" :key="action.label"
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-blue-text text-primary-blue-text hover:bg-primary-blue-bg-light transition">
                            {{ action.icon }} {{ action.label }}
                        </button>
                    </div>
                </div>

                <div class="overflow-x-auto border border-slate-100 rounded-xl">
                    <table class="min-w-full divide-y divide-slate-100 text-xs sm:text-sm">
                        <thead class="bg-slate-50 text-slate-500 font-semibold">
                            <tr>
                                <th class="px-4 py-3 text-left">Student ID</th>
                                <th class="px-4 py-3 text-left">Student Name</th>
                                <th class="px-4 py-3 text-left">Program</th>
                                <th class="px-4 py-3 text-left">Course & Title</th>
                                <th class="px-4 py-3 text-center">Attempts</th>
                                <th class="px-4 py-3 text-center">GPA</th>
                                <th class="px-4 py-3 text-center">Status</th>
                                <th class="px-4 py-3 text-left">Current Instructor</th>
                                <th class="px-4 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100 text-slate-700">
                            <tr v-for="student in urgentList" :key="student.studentId"
                                class="hover:bg-slate-50 transition">
                                <td class="px-4 py-3 font-semibold text-slate-900">{{ student.studentId }}</td>
                                <td class="px-4 py-3">
                                    <div class="font-semibold text-slate-900">{{ student.name }}</div>
                                    <p class="text-xs text-slate-500">Cohort {{ student.cohort }}</p>
                                </td>
                                <td class="px-4 py-3">
                                    <p class="font-medium">{{ student.program }}</p>
                                    <p class="text-xs text-slate-500">{{ student.entity }}</p>
                                </td>
                                <td class="px-4 py-3">
                                    <p class="font-medium">{{ student.course }}</p>
                                    <p class="text-xs text-slate-500">Attempts: {{ student.attempts }}</p>
                                    <div class="w-full h-1.5 bg-slate-100 rounded-full mt-2">
                                        <div class="h-full rounded-full bg-rose-500"
                                            :style="{ width: student.attemptRate + '%' }"></div>
                                    </div>
                                </td>
                                <td class="px-4 py-3 text-center font-semibold">{{ student.attempts }}</td>
                                <td class="px-4 py-3 text-center font-semibold text-amber-600">{{ student.gpa }}</td>
                                <td class="px-4 py-3 text-center">
                                    <span :class="statusBadgeClass(student.status)">
                                        {{ student.status }}
                                    </span>
                                </td>
                                <td class="px-4 py-3">
                                    <p class="font-medium">{{ student.instructor }}</p>
                                    <p class="text-xs text-slate-500">{{ student.mode }}</p>
                                </td>
                                <td class="px-4 py-3">
                                    <div class="flex flex-wrap gap-2">
                                        <button v-for="action in student.actions" :key="action"
                                            class="px-3 py-1 text-xs font-semibold rounded-full border border-slate-200 hover:bg-slate-100 transition">
                                            {{ action }}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
    name: "FailureInsightsDashboard",
    components: {
        Multiselect
    },
    data() {
        return {
            showFilter: false,
            campusList: [],
            programList: [],
            failedTermList: ["2023-24", "2022-23", "2021-22", "2020-21"],
            instructorList: [],
            filters: {
                risk_level: "",
                entity: "",
                campus_code: [],
                program: [],
                gender: "",
                failed_term: [],
                current_reg: "",
                instructor: []
            },
            kpis: [
                {
                    title: "Students At-Risk",
                    value: "156",
                    subtitle: "Active watchlist",
                    trendText: "+8 vs last term",
                    trendDirection: "up",
                    icon: "⚠️",
                    accent: "text-rose-500"
                },
                {
                    title: "High Risk Students",
                    value: "84",
                    subtitle: "Immediate outreach",
                    trendText: "+3 escalations",
                    trendDirection: "up",
                    icon: "🔥",
                    accent: "text-orange-500"
                },
                {
                    title: "Currently Registered (2nd Attempt)",
                    value: "41",
                    subtitle: "Need proactive support",
                    trendText: "-5 cleared this week",
                    trendDirection: "down",
                    icon: "📘",
                    accent: "text-blue-500"
                },
                {
                    title: "Currently Registered (3rd Attempt)",
                    value: "5",
                    subtitle: "Critical escalation",
                    trendText: "Unchanged",
                    trendDirection: "steady",
                    icon: "⏱️",
                    accent: "text-slate-500"
                }
            ],
            courseFailureData: [
                { course: "GRD Biology", count: 45 },
                { course: "GRD Anatomy & Phys", count: 38 },
                { course: "BSN Fundamentals Nursing", count: 22 },
                { course: "EHS Medical Emergencies", count: 18 }
            ],
            programRiskData: [
                { program: "BSc in Nursing", score: 30 },
                { program: "Diploma Emerg. Health", score: 20 },
                { program: "Bachelor Medical Imaging", score: 15 },
                { program: "BSc in Psychology", score: 10 }
            ],
            instructorFailureData: [
                { instructor: "Alexandra Dimitri", count: 17 },
                { instructor: "Mohammed Alhashmi", count: 14 },
                { instructor: "Sarah Johnson", count: 12 },
                { instructor: "Ahmed Hassan", count: 10 }
            ],
            campusFailures: [
                { name: "FAD Campus", percentage: 55, barClass: "bg-rose-500" },
                { name: "FAA Campus", percentage: 35, barClass: "bg-amber-500" },
                { name: "FQA Campus", percentage: 10, barClass: "bg-blue-500" }
            ],
            urgentList: [
                {
                    studentId: "V00023115",
                    name: "Hamad Alahmadi",
                    cohort: "2024",
                    program: "BSc in Nursing",
                    entity: "ADPOLY",
                    course: "GRD - Biology",
                    attempts: "2/2",
                    attemptRate: 100,
                    gpa: 2.43,
                    status: "High Risk",
                    instructor: "Mohammed Hlayel",
                    mode: "On-campus",
                    actions: ["Alert", "Email", "Schedule"]
                },
                {
                    studentId: "A00611348",
                    name: "Maryam Almessabi",
                    cohort: "2023",
                    program: "BSc in Nursing",
                    entity: "FCHS",
                    course: "BSN 212 - Fundamentals",
                    attempts: "2/2",
                    attemptRate: 100,
                    gpa: 1.89,
                    status: "High Risk",
                    instructor: "Nuru Hasan",
                    mode: "Hybrid",
                    actions: ["Alert", "Advisor"]
                },
                {
                    studentId: "A00051935",
                    name: "Amna Alteneiji",
                    cohort: "2023",
                    program: "BSc in Psychology",
                    entity: "FCHS",
                    course: "GRD 121 - Anatomy",
                    attempts: "2/2",
                    attemptRate: 100,
                    gpa: 2.15,
                    status: "High Risk",
                    instructor: "Ahmed Saleh",
                    mode: "Online",
                    actions: ["Alert", "Email"]
                }
            ],
            quickActions: [
                { label: "Export to Excel", icon: "⬇️" },
                { label: "Send Alerts to Advisors", icon: "📨" },
                { label: "Generate Program Report", icon: "📊" }
            ]
        };
    },
    computed: {
        courseFailureSeries() {
            return [
                {
                    name: "Fail Count",
                    data: this.courseFailureData.map(function (item) {
                        return item.count;
                    })
                }
            ];
        },
        courseFailureOptions() {
            return {
                chart: { toolbar: { show: false } },
                colors: ["#ef4444"],
                plotOptions: {
                    bar: { horizontal: false, columnWidth: "45%", endingShape: "rounded" }
                },
                dataLabels: { enabled: false },
                xaxis: {
                    categories: this.courseFailureData.map(function (item) {
                        return item.course;
                    }),
                    labels: { style: { colors: "#475569", fontSize: "12px" } }
                },
                yaxis: {
                    labels: { style: { colors: "#94a3b8" } },
                    title: { text: "Students", style: { color: "#94a3b8" } }
                },
                grid: { borderColor: "#e2e8f0" },
                tooltip: { theme: "light" }
            };
        },
        programRiskSeries() {
            return [
                {
                    name: "Risk Score",
                    data: this.programRiskData.map(function (item) {
                        return item.score;
                    })
                }
            ];
        },
        programRiskOptions() {
            return {
                chart: { toolbar: { show: false } },
                colors: ["#3b82f6"],
                plotOptions: {
                    bar: { horizontal: true, barHeight: "60%", borderRadius: 6 }
                },
                dataLabels: { enabled: true, offsetX: 10, style: { colors: ["#0f172a"] } },
                xaxis: {
                    categories: this.programRiskData.map(function (item) {
                        return item.program;
                    }),
                    labels: { style: { colors: "#475569", fontSize: "11px" } }
                },
                grid: { borderColor: "#e2e8f0" }
            };
        },
        instructorFailureSeries() {
            return [
                {
                    name: "Failures",
                    data: this.instructorFailureData.map(function (item) {
                        return item.count;
                    })
                }
            ];
        },
        instructorFailureOptions() {
            return {
                chart: { toolbar: { show: false } },
                colors: ["#a855f7"],
                plotOptions: {
                    bar: { horizontal: true, barHeight: "55%", borderRadius: 6 }
                },
                dataLabels: { enabled: true, offsetX: 10, style: { colors: ["#0f172a"] } },
                xaxis: {
                    categories: this.instructorFailureData.map(function (item) {
                        return item.instructor;
                    }),
                    labels: { style: { colors: "#475569", fontSize: "12px" } }
                },
                grid: { borderColor: "#e2e8f0" }
            };
        },
        campusFailureSeries() {
            return this.campusFailures.map(function (campus) {
                return campus.percentage;
            });
        },
        campusFailureOptions() {
            return {
                chart: { toolbar: { show: false } },
                labels: this.campusFailures.map(function (campus) {
                    return campus.name;
                }),
                colors: ["#fb7185", "#fbbf24", "#60a5fa"],
                legend: { position: "bottom" },
                dataLabels: { style: { fontSize: "12px" } }
            };
        },
        campusOptionsFiltered() {
            var campuses = this.campusList;
            if (this.filters.entity) {
                campuses = campuses.filter(
                    (campus) => campus.entity === this.filters.entity
                );
            }
            return campuses.map(function (campus) {
                return {
                    id: campus.campus_code,
                    name: campus.campus_code
                };
            });
        },
        programOptions() {
            return this.programList.map(function (program) {
                return {
                    id: program.program_name,
                    name: program.program_name
                };
            });
        },
        failedTermOptions() {
            return this.failedTermList.map(function (term) {
                return {
                    id: term,
                    name: term
                };
            });
        },
        instructorOptions() {
            return this.instructorList.map(function (instructor) {
                return {
                    id: instructor.instructor_name,
                    name: instructor.instructor_name
                };
            });
        }
    },
    created() {
        this.getCampusList();
        this.getProgramList();
        this.getInstructorList();
    },
    methods: {
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.toggleFilter();
            this.filters = {
                risk_level: "",
                entity: "",
                campus_code: [],
                program: [],
                gender: "",
                failed_term: [],
                current_reg: "",
                instructor: []
            };
            this.loadChartData();
        },
        applyFilters() {
            this.toggleFilter();
            this.loadChartData();
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetCampusList($student_status: String, $entity: String) {
                            getCampusList(student_status: $student_status, entity: $entity) {
                                entity
                                campus_code
                            }
                        }
                    `,
                    variables: {
                        student_status: "IG",
                        entity: "",
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
        async getProgramList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetProgramList {
                            getProgramList {
                                program_name
                            }
                        }
                    `,
                    fetchPolicy: "network-only",
                });
                if (data && data.getProgramList) {
                    this.programList = data.getProgramList;
                } else {
                    this.programList = [
                        { program_name: "BSc in Nursing" },
                        { program_name: "Diploma Emerg. Health" },
                        { program_name: "Bachelor Medical Imaging" },
                        { program_name: "BSc in Psychology" }
                    ];
                }
            } catch (error) {
                console.log(error);
                this.programList = [
                    { program_name: "BSc in Nursing" },
                    { program_name: "Diploma Emerg. Health" },
                    { program_name: "Bachelor Medical Imaging" },
                    { program_name: "BSc in Psychology" }
                ];
            }
        },
        async getInstructorList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetInstructorList {
                            getInstructorList {
                                instructor_name
                            }
                        }
                    `,
                    fetchPolicy: "network-only",
                });
                if (data && data.getInstructorList) {
                    this.instructorList = data.getInstructorList;
                } else {
                    this.instructorList = [
                        { instructor_name: "Alexandra Dimitri" },
                        { instructor_name: "Mohammed Alhashmi" },
                        { instructor_name: "Sarah Johnson" },
                        { instructor_name: "Ahmed Hassan" },
                        { instructor_name: "Mohammed Hlayel" },
                        { instructor_name: "Nuru Hasan" },
                        { instructor_name: "Ahmed Saleh" }
                    ];
                }
            } catch (error) {
                console.log(error);
                this.instructorList = [
                    { instructor_name: "Alexandra Dimitri" },
                    { instructor_name: "Mohammed Alhashmi" },
                    { instructor_name: "Sarah Johnson" },
                    { instructor_name: "Ahmed Hassan" },
                    { instructor_name: "Mohammed Hlayel" },
                    { instructor_name: "Nuru Hasan" },
                    { instructor_name: "Ahmed Saleh" }
                ];
            }
        },
        loadChartData() {
            var filterValues = {
                risk_level: this.filters.risk_level,
                entity: this.filters.entity,
                campus_code: this.extractIds(this.filters.campus_code),
                program: this.extractIds(this.filters.program),
                gender: this.filters.gender,
                failed_term: this.extractIds(this.filters.failed_term),
                current_reg: this.filters.current_reg,
                instructor: this.extractIds(this.filters.instructor)
            };
            console.log("Loading chart data with filters:", filterValues);
        },
        extractIds(items) {
            if (!items || !Array.isArray(items)) {
                return [];
            }
            return items.map(function (item) {
                if (typeof item === 'object' && item !== null && item.id) {
                    return item.id;
                }
                return item;
            });
        },
        getTrendIcon(direction) {
            if (direction === "up") {
                return "↑";
            }
            if (direction === "down") {
                return "↓";
            }
            return "→";
        },
        getTrendColorClass(direction) {
            if (direction === "up") {
                return "text-emerald-600";
            }
            if (direction === "down") {
                return "text-rose-600";
            }
            return "text-slate-500";
        },
        statusBadgeClass(status) {
            var base = "px-3 py-1 rounded-full text-xs font-semibold";
            if (status === "High Risk") {
                return base + " bg-rose-100 text-rose-600";
            }
            if (status === "Moderate Risk") {
                return base + " bg-amber-100 text-amber-600";
            }
            return base + " bg-emerald-100 text-emerald-600";
        }
    }
};
</script>

<style lang="scss" scoped>
.text-primary-blue-text {
    color: #1d337c;
}

.bg-primary-blue-bg-light {
    background-color: rgba(73, 157, 212, 0.08);
}

.border-primary-blue-text {
    border-color: #1d337c;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

:deep(.multiselect) {
    min-height: 36px;
}

:deep(.multiselect__tags) {
    min-height: 36px;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    padding: 8px 40px 0 8px;
}

:deep(.multiselect--active .multiselect__tags),
:deep(.multiselect:hover .multiselect__tags) {
    border-color: #3b82f6;
}

:deep(.multiselect__option--highlight) {
    background: #3b82f6;
}

:deep(.multiselect__option--highlight:after) {
    background: #3b82f6;
}

:deep(.multiselect__content-wrapper) {
    border: 1px solid #d1d5db;
    border-top: none;
}
</style>
